import { useState } from 'react'
import { Check, X } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'
import { showToast } from './Toast'
import { api, ApiError } from '../services/api'
import { useCoursesByStore } from '../hooks/useCourses'
import StripeCheckout from './StripeCheckout'
import type { Reservation } from '../store/useStore'

interface Props {
  open: boolean
  onClose: () => void
  reservation: Reservation
  storeName: string
}

type IncrementalPI = {
  clientSecret: string
  paymentIntentId: string
  amount: number
  captureMethod: 'automatic' | 'manual'
  connectedAccountId: string
}

type Step = 'select' | 'payment'

/**
 * #745 Phase 2.3: 予約コースを変更するモーダル.
 *
 * フロー:
 *  1. select step — 同 store の active コース一覧から選択 + 差額 preview
 *  2. submit
 *     - 減額: 部分返金完了 → toast → close
 *     - 増額: 増分 PI 返却 → payment step に遷移 → 決済成功 → close
 *  3. status は 'pending' に戻り店主再承認待ち
 */
export default function ChangeCourseModal({ open, onClose, reservation, storeName }: Props) {
  const { t } = useI18n()
  const { courses } = useCoursesByStore(reservation.storeId)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [step, setStep] = useState<Step>('select')
  const [incrementalPI, setIncrementalPI] = useState<IncrementalPI | null>(null)

  // open 切替で state リセット
  const [prevOpen, setPrevOpen] = useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) {
      setSelectedId(null)
      setReason('')
      setStep('select')
      setIncrementalPI(null)
    }
  }

  if (!open) return null

  // 旧コース除外 + party_size が範囲内のもののみ表示
  const availableCourses = courses.filter(c => {
    if (c.id === reservation.courseId) return false
    const range = c.partySizeRange
    return reservation.partySize >= range.min && reservation.partySize <= range.max
  })
  const selected = availableCourses.find(c => c.id === selectedId)
  const oldTotal = reservation.totalAmount ?? 0
  const newTotal = selected ? selected.priceYen * reservation.partySize : 0
  const diff = selected ? newTotal - oldTotal : 0

  // ChangePartySizeModal と同じ paymentImpact 分類 (= backend 実挙動と整合)
  const paymentImpact: 'paid' | 'authorized' | 'unpaid' = (() => {
    if (reservation.paymentStatus === 'paid' || reservation.paymentStatus === 'partially_refunded') {
      return 'paid'
    }
    if (
      reservation.paymentTiming === 'onsite' &&
      reservation.captureMethod === 'manual' &&
      reservation.paymentStatus === 'authorized'
    ) {
      return 'authorized'
    }
    return 'unpaid'
  })()

  const handleSubmit = async () => {
    if (!selected || submitting) return
    setSubmitting(true)
    try {
      const res = await api.changeReservationCourse(reservation.id, selected.id, reason.trim() || undefined)
      if (res.incrementalPaymentIntent) {
        setIncrementalPI(res.incrementalPaymentIntent)
        setStep('payment')
        return
      }
      if (typeof res.refundAmount === 'number' && res.refundAmount > 0) {
        showToast({
          type: 'success',
          message: t('changeCourse.refunded', { amount: res.refundAmount.toLocaleString() }),
        })
      } else {
        showToast({ type: 'success', message: t('changeCourse.updated') })
      }
      onClose()
    } catch (e) {
      const detail = (e as Error)?.message || ''
      // 管理番号040: 締切超過は構造化 detail (code) で返る。400 の message は
      // apiFetch が汎用文言に差し替えるので、message の文字列一致では判定できない。
      const code = (e as ApiError)?.code
      console.error('[ChangeCourse] failed:', e)
      let msg = t('changeCourse.failed')
      if (code === 'change_deadline_passed') msg = t('changeCourse.deadlinePassed')
      else if (code === 'change_pending') msg = t('myReservations.changeLockedPending')
      else if (detail.includes('outside')) msg = t('changeCourse.partySizeOutOfRange')
      else if (detail.includes('taken')) msg = t('changeCourse.slotConflict')
      showToast({ type: 'error', message: msg })
    } finally {
      setSubmitting(false)
    }
  }

  // 増分決済 step
  if (step === 'payment' && incrementalPI && selected) {
    return (
      <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/50 px-4 animate-fade-in">
        <div className="w-full max-w-sm rounded-2xl bg-white p-5 max-h-[90vh] overflow-y-auto animate-scale-in">
          <h3 className="font-display text-lg font-bold text-text-primary mb-3">
            {t('changeCourse.paymentStepTitle')}
          </h3>
          <p className="font-body text-xs text-text-secondary mb-4">
            {t('changeCourse.paymentStepHint', { amount: incrementalPI.amount.toLocaleString() })}
          </p>
          <StripeCheckout
            clientSecret={incrementalPI.clientSecret}
            amount={incrementalPI.amount}
            storeName={storeName}
            connectedAccountId={incrementalPI.connectedAccountId}
            paymentIntentId={incrementalPI.paymentIntentId}
            storeId={reservation.storeId}
            captureMethod={incrementalPI.captureMethod}
            onSuccess={() => {
              showToast({ type: 'success', message: t('changeCourse.paymentComplete') })
              onClose()
            }}
            onCancel={() => {
              showToast({ type: 'info', message: t('changeCourse.paymentDeferred') })
              onClose()
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/50 px-4 animate-fade-in">
      <div className="w-full max-w-sm rounded-2xl bg-white p-5 max-h-[85vh] overflow-y-auto animate-scale-in">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-bold text-text-primary">
            {t('changeCourse.title')}
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 tap-effect"
            aria-label="close"
          >
            <X size={16} className="text-text-secondary" />
          </button>
        </div>

        <p className="font-body text-xs text-text-tertiary mb-1">{storeName}</p>
        <p className="font-body text-xs text-text-tertiary mb-4">
          {t('changeCourse.previous', { name: reservation.courseName || '-' })}
        </p>

        {availableCourses.length === 0 ? (
          <p className="font-body text-sm text-text-tertiary py-8 text-center">
            {t('changeCourse.noAlternatives')}
          </p>
        ) : (
          <div className="space-y-2 mb-4">
            {availableCourses.map(c => (
              <button
                key={c.id}
                onClick={() => setSelectedId(c.id)}
                className={`w-full flex items-start gap-2 text-left rounded-lg border px-3 py-2.5 tap-effect transition-colors ${
                  selectedId === c.id ? 'border-yokoso-red bg-yokoso-red/5' : 'border-gray-200 bg-white'
                }`}
              >
                <div
                  className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                    selectedId === c.id ? 'border-yokoso-red bg-yokoso-red' : 'border-gray-300'
                  }`}
                >
                  {selectedId === c.id && <Check size={10} className="text-white" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-body text-sm font-semibold text-text-primary line-clamp-2 leading-tight">
                    {c.name}
                  </p>
                  <p className="font-body text-xs text-text-secondary mt-0.5">
                    ¥{c.priceYen.toLocaleString()} × {reservation.partySize} 名 = ¥{(c.priceYen * reservation.partySize).toLocaleString()}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* #1644 飲み放題プラン付き予約のコース変更はプランが外れる (新コースの
            選択肢は別物)。黙って外すと客は当日「飲み放題のはず」と揉めるので明示 */}
        {selected && reservation.drinkPlanName && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 mb-3">
            <p className="font-body text-xs text-text-primary leading-relaxed">
              {t('changeCourse.dropDrinkPlanNotice', { name: reservation.drinkPlanName })}
            </p>
          </div>
        )}

        {selected && diff !== 0 && (() => {
          const absDiff = Math.abs(diff)
          let messageKey: string
          let bgClass: string
          if (paymentImpact === 'paid') {
            // #1644 手付金モードの減額は「abs(diff) を返金」ではない — 前受金は
            // 会計時に充当され、新合計を上回った分だけ返金される (サーバと同じ規則)
            if (diff < 0 && reservation.paymentTiming === 'deposit') {
              messageKey = 'changeCourse.previewDecreaseDeposit'
              bgClass = 'bg-matcha/10'
            } else {
              messageKey = diff > 0 ? 'changeCourse.previewIncrease' : 'changeCourse.previewDecrease'
              bgClass = diff > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-matcha/10'
            }
          } else if (paymentImpact === 'authorized') {
            messageKey = diff > 0 ? 'changeCourse.previewIncreaseAuthorized' : 'changeCourse.previewDecreaseAuthorized'
            bgClass = 'bg-amber-50 border border-amber-200'
          } else {
            messageKey = diff > 0 ? 'changeCourse.previewIncreaseUnpaid' : 'changeCourse.previewDecreaseUnpaid'
            bgClass = 'bg-gray-50 border border-gray-200'
          }
          return (
            <div className={`rounded-lg p-3 mb-4 ${bgClass}`}>
              <p className="font-body text-xs text-text-primary leading-relaxed">
                {t(messageKey, { amount: absDiff.toLocaleString() })}
              </p>
            </div>
          )
        })()}

        <p className="font-body text-sm font-semibold text-text-primary mb-2">
          {t('changeCourse.reasonLabel')}
        </p>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder={t('changeCourse.reasonPlaceholder')}
          rows={2}
          className="w-full bg-gray-50 rounded-lg px-3 py-2 font-body text-sm text-text-primary outline-none focus:ring-2 focus:ring-yokoso-red/30 mb-4 resize-none"
        />

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 h-11 rounded-xl border border-gray-200 text-sm font-body font-semibold text-text-secondary"
          >
            {t('changeCourse.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selected || submitting}
            className={`flex-1 h-11 rounded-xl text-sm font-body font-semibold transition-colors ${
              !selected || submitting ? 'bg-gray-300 text-white' : 'bg-yokoso-red text-white tap-effect'
            }`}
          >
            {submitting ? '...' : diff > 0 ? t('changeCourse.confirmIncrease') : t('changeCourse.confirmDecrease')}
          </button>
        </div>
      </div>
    </div>
  )
}
